import type { ReactNode } from 'react';
import type { UploadedFile } from '../types';
import { useState } from 'react';
import css from './upload-button.module.scss';
import { UploadDropzone } from './upload-dropzone';

export type UploadButtonProps = {
  startUpload: (files: File[]) => Promise<UploadedFile[] | undefined>;
  isUploading?: boolean;
  progress?: number;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  // Closes the modal when the consumer's startUpload promise resolves.
  // The actual upload-complete callback comes from `useUploadThing`.
  closeOnSuccess?: boolean;
  label?: ReactNode;
  icon?: ReactNode;
  dropzonePrompt?: ReactNode;
  dropzoneHint?: ReactNode;
  modalTitle?: ReactNode;
  // Render-prop override for the trigger — when supplied, the default
  // tile button is replaced.
  renderTrigger?: (open: () => void) => ReactNode;
};

export const UploadButton = ({
  startUpload,
  isUploading,
  progress,
  accept,
  multiple,
  disabled,
  closeOnSuccess = true,
  label = 'Upload',
  icon,
  dropzonePrompt,
  dropzoneHint,
  modalTitle = 'Upload files',
  renderTrigger,
}: UploadButtonProps) => {
  const [open, setOpen] = useState(false);

  const handleUpload = async (files: File[]): Promise<undefined> => {
    await startUpload(files);
    if (closeOnSuccess) setOpen(false);
    return undefined;
  };

  const openModal = () => setOpen(true);
  const trigger = renderTrigger ? (
    renderTrigger(openModal)
  ) : (
    <button
      className={css.button}
      disabled={disabled}
      onClick={openModal}
      type="button"
    >
      {icon && <span className={css.buttonIcon}>{icon}</span>}
      <span>{label}</span>
    </button>
  );

  return (
    <>
      {trigger}
      {open && (
        <div
          aria-modal="true"
          className={css.backdrop}
          onClick={(event) => {
            if (event.target === event.currentTarget) setOpen(false);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setOpen(false);
          }}
          role="dialog"
          tabIndex={-1}
        >
          <div className={css.modal}>
            <div className={css.modalHeader}>
              <span className={css.modalTitle}>{modalTitle}</span>
              <button
                aria-label="Close"
                className={css.modalClose}
                onClick={() => setOpen(false)}
                type="button"
              >
                ×
              </button>
            </div>
            <UploadDropzone
              accept={accept}
              disabled={disabled}
              hint={dropzoneHint}
              isUploading={isUploading}
              multiple={multiple}
              progress={progress}
              prompt={dropzonePrompt}
              startUpload={handleUpload}
            />
          </div>
        </div>
      )}
    </>
  );
};
